import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, ListGroup } from "react-bootstrap";
import { FiFileText, FiMessageSquare, FiPlus, FiSearch } from "react-icons/fi";
import { GoTasklist } from "react-icons/go";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import ChatComposer from "./ChatComposer";
import ThreadPanel from "./ThreadPanel";
import ProjectManagement from "../../ProjectManagement/ProjectManagement";
import {
  ChatSocket,
  convIdOf,
  groupConvId,
  checkedAssignedUser,
  setGroupMembers,
} from "../../../APIServices/GoApiServices/chatService";
import { groupAndProjectServices } from "../../../APIServices/WorkBridge/TeamManagement/groupAndProjectServices";

function getUserName(user, fallbackId) {
  return user?.name || user?.fullName || user?.email || fallbackId;
}

function pickLastIncomingFrom(messages, peerId) {
  return [...(messages || [])].reverse().find((m) => m?.from === peerId);
}

function pickLastIncomingNotMe(messages, meId) {
  return [...(messages || [])].reverse().find((m) => m?.from && m?.from !== meId);
}

function uniqStrings(list) {
  const out = [];
  const seen = new Set();
  for (const x of list || []) {
    const v = String(x ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function toId(value) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") return String(value.value ?? value.userId ?? value.id ?? "").trim();
  return "";
}

function buildMembersNameMap({ selectedMemberIds, members, meId, meName }) {
  const map = new Map();

  for (const item of selectedMemberIds || []) {
    const id = toId(item);
    if (!id) continue;
    const label = typeof item === "object" ? String(item.label ?? item.name ?? "").trim() : "";
    if (label) map.set(id, label);
  }

  for (const m of members || []) {
    const id = String(m?.userId ?? m?.id ?? "").trim();
    if (!id) continue;
    const name = String(m?.name ?? m?.fullName ?? m?.email ?? "").trim();
    if (name && !map.has(id)) map.set(id, name);
  }

  if (meId) map.set(String(meId), String(meName || meId));
  return map;
}

const ChatLayout = ({
  channelName,
  setSelectedProfile,
  setActiveThread,
  activeThread,
  members,
  currentUserData,
  selectedMemberIds = [],
  assignCheck,
  getBookmarkTeamProjectList
}) => {
  const [activeTab, setActiveTab] = useState("messages");
  const [showAll, setShowAll] = useState(false);
  const [messagesState, setMessagesState] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [groupAllowed, setGroupAllowed] = useState(true);
  const [groupTypingIds, setGroupTypingIds] = useState([]);
  const [sockNonce, setSockNonce] = useState(0);
  const meId = currentUserData?.userId;
  const meName = getUserName(currentUserData, meId);
  const isGroup = Boolean(channelName?.gpId) && !channelName?.userId;
  const groupId = isGroup ? channelName?.gpId : `${currentUserData?.userId}_${channelName?.userId}`;
  const peerId = !isGroup ? channelName?.userId : "";
  const peerName = !isGroup ? (channelName?.name || peerId) : "";

  const convId = useMemo(() => {
    if (isGroup) return groupConvId(groupId);
    return convIdOf(meId, peerId);
  }, [isGroup, groupId, meId, peerId]);

  const sockRef = useRef(null);

  const joinTimerRef = useRef(null);
  const lastJoinKeyRef = useRef("");
  const groupJoinedRef = useRef(false);
  const warmSyncRef = useRef(false);

  const typingTimersRef = useRef(new Map());

  const chatToken =
    currentUserData?.token ||
    currentUserData?.sessionToken ||
    localStorage.getItem("token") ||
    "";

  const membersNameMap = useMemo(() => {
    return buildMembersNameMap({ selectedMemberIds, members, meId, meName });
  }, [selectedMemberIds, members, meId, meName]);

  const resolveUserName = useCallback(
    (userId) => {
      const id = String(userId ?? "").trim();
      if (!id) return "";
      return membersNameMap.get(id) || id;
    },
    [membersNameMap]
  );

  const setUserTyping = useCallback((userId, typing) => {
    const uid = String(userId ?? "").trim();
    if (!uid) return;

    const timers = typingTimersRef.current;

    const old = timers.get(uid);
    if (old) clearTimeout(old);

    if (!typing) {
      timers.delete(uid);
      setGroupTypingIds((prev) => prev.filter((x) => x !== uid));
      return;
    }

    setGroupTypingIds((prev) => (prev.includes(uid) ? prev : [...prev, uid]));

    const t = setTimeout(() => {
      timers.delete(uid);
      setGroupTypingIds((prev) => prev.filter((x) => x !== uid));
    }, 3000);

    timers.set(uid, t);
  }, []);

  useEffect(() => {
    if (!isGroup) return;

    groupJoinedRef.current = false;
    warmSyncRef.current = false;
    setGroupTypingIds([]);

    if (joinTimerRef.current) {
      clearInterval(joinTimerRef.current);
      joinTimerRef.current = null;
    }
    lastJoinKeyRef.current = "";

    for (const t of typingTimersRef.current.values()) clearTimeout(t);
    typingTimersRef.current.clear();
  }, [isGroup, convId, groupId]);

  const stopJoinTimer = useCallback(() => {
    if (joinTimerRef.current) {
      clearInterval(joinTimerRef.current);
      joinTimerRef.current = null;
    }
  }, []);

  const getMessageAndFile = async () => {
    setMessagesState([])
    try {
      let gpId;
      if (channelName?.gpId) {
        gpId = channelName.gpId;
      } else {
        gpId = `${currentUserData.userId}_${channelName.userId}`;
      }
      const res = await groupAndProjectServices.getMessageAndFile(gpId);
      setMessagesState(res?.data || []);
    } catch (err) {
      setMessagesState([]);
    }
  };

  useEffect(() => {
    if (!channelName || !currentUserData) return;
    getMessageAndFile();
  }, [channelName, currentUserData]);

  const reverseGpId = (gpId) => {
    return gpId.split("_").reverse().join("_");
  };

  const handleSocketEvent = useCallback(
    (type, data) => {
      console.log(">data", type, data);
      let uniqueId = `${data?.convId.split("_")[1]}_${data?.convId.split("_")[2]}`
      if (type === "message:new") {

        const isForThisChat = (data?.groupId === groupId || uniqueId === groupId || uniqueId === reverseGpId(groupId));
        if (isForThisChat) {
          let message = {
            _id: data?._id,
            adminId: data?.adminId,
            accountId: data?.accountId,
            gpId: data?.gpId || uniqueId,
            messageId: data?.messageId,
            senderId: data?.senderId,
            messageType: data?.messageType,
            message: data?.message,
            files: data?.files || [],
            isUpdate: data?.isUpdate || false,
            seenedBy: data?.seenedBy || [],
            createdAt: data?.createdAt,
            updatedAt: data?.updatedAt,
          }
          setMessagesState((prev) => [...prev, message]);
        }
        return;
      }

      if (isGroup && type === "group:joined") {
        const joinedConv = String(data?.convId ?? "").trim();
        const joinedGid = String(data?.groupId ?? "").trim();
        if (joinedConv === convId && joinedGid === groupId) {
          groupJoinedRef.current = true;
          stopJoinTimer();
          if (!warmSyncRef.current) {
            warmSyncRef.current = true;
            setTimeout(async () => {
              getMessageAndFile()
            }, 350);
          }
        }
        return;
      }

      if (isGroup && type === "group:typing") {
        const incomingConvId = String(data?.convId ?? "").trim();
        const incomingGroupId = String(data?.groupId ?? "").trim();

        const isForThisGroup = incomingConvId === convId || incomingGroupId === groupId;
        if (!isForThisGroup) return;

        const from = String(data?.from ?? "").trim();
        if (!from) return;

        if (String(meId ?? "").trim() && from === String(meId ?? "").trim()) return;

        setUserTyping(from, !!data?.isTyping);
        return;
      }

      // DM only
      if (!isGroup && type === "message:status") {
        setMessagesState((prev) =>
          prev.map((m) => (m?.id === data?.messageId ? { ...m, status: data?.status } : m))
        );
        return;
      }

      if (!isGroup && type === "typing:update") {
        if (String(data?.convId ?? "").trim() !== convId) return;
        if (data?.from === peerId) setIsTyping(!!data?.isTyping);
      }
    },
    [convId, isGroup, peerId, groupId, stopJoinTimer, chatToken, meId, setUserTyping]
  );

  useEffect(() => {
    if (!chatToken) return;

    const sock = new ChatSocket({
      token: chatToken,
      name: meName || "",
      onEvent: handleSocketEvent,
    });

    sock.connect();
    sockRef.current = sock;

    setSockNonce((n) => n + 1);

    return () => {
      stopJoinTimer();

      for (const t of typingTimersRef.current.values()) clearTimeout(t);
      typingTimersRef.current.clear();

      sock.disconnect();
      sockRef.current = null;
    };
  }, [chatToken, handleSocketEvent, meName, stopJoinTimer]);

  useEffect(() => {
    let alive = true;

    async function ensureAllowed() {
      if (!isGroup) {
        setGroupAllowed(true);
        return true;
      }

      if (!chatToken || !meId || !groupId || !convId) {
        setGroupAllowed(false);
        return false;
      }

      if (assignCheck && typeof assignCheck?.assigned === "boolean") {
        if (!assignCheck.assigned) {
          setGroupAllowed(false);
          return false;
        }
      } else {
        try {
          const res = await checkedAssignedUser(groupId, meId, chatToken);
          if (!alive) return false;
          if (!res?.assigned) {
            setGroupAllowed(false);
            return false;
          }
        } catch {
          if (!alive) return false;
        }
      }

      setGroupAllowed(true);
      return true;
    }

    async function startJoinRetry() {
      if (!isGroup) return;
      const ok = await ensureAllowed();
      if (!alive || !ok) return;

      const selectedIds = (selectedMemberIds || []).map(toId).filter(Boolean);
      const membersList = uniqStrings([...selectedIds, meId]);

      const joinKey = `${groupId}|${convId}|${membersList.join(",")}`;
      if (lastJoinKeyRef.current === joinKey && joinTimerRef.current) return;
      lastJoinKeyRef.current = joinKey;

      stopJoinTimer();
      groupJoinedRef.current = false;

      let attempts = 0;
      const maxAttempts = 30;

      const tryJoin = async () => {
        if (!alive) return;
        if (groupJoinedRef.current) {
          stopJoinTimer();
          return;
        }

        const sock = sockRef.current;
        if (!sock) return;

        try {
          await setGroupMembers(groupId, membersList, chatToken);
        } catch {
        }

        sock.joinGroup({ convId, groupId, members: membersList });

        attempts += 1;
        if (attempts >= maxAttempts) stopJoinTimer();
      };

      await tryJoin();
      joinTimerRef.current = setInterval(tryJoin, 600);
    }

    startJoinRetry();

    return () => {
      alive = false;
    };
  }, [isGroup, groupId, convId, chatToken, meId, selectedMemberIds, assignCheck, sockNonce, stopJoinTimer]);

  // mark seen
  useEffect(() => {
    if (!sockRef.current || !convId) return;

    if (!isGroup) {
      if (!peerId) return;
      const lastIncoming = pickLastIncomingFrom(messagesState, peerId);
      if (!lastIncoming) return;
      if (lastIncoming?.status === "seen") return;
      sockRef.current.markSeen(convId, peerId, lastIncoming.id);
      return;
    }

    if (!meId) return;
    const lastIncoming = pickLastIncomingNotMe(messagesState, meId);
    if (!lastIncoming?.id) return;
    sockRef.current.markGroupSeen(convId, lastIncoming.id);
  }, [messagesState, convId, peerId, isGroup, meId]);

  const handleSendMessage = useCallback(
    async ({ text, files }) => {
      if ((!text || !text.trim()) && (!files || files.length === 0)) return;
      if (!meId || !convId) return;
      let userData = {}
      if (channelName?.gpId) {
        userData.gpId = channelName?.gpId
      } else {
        userData.gpId = `${currentUserData.userId}_${channelName.userId}`
      }
      userData.senderId = currentUserData?.userId
      userData.receiverId = channelName?.userId
      userData.messageType = "message"
      userData.message = text
      let data = await groupAndProjectServices.addNewMessageAndFile(userData);
      sockRef.current?.typingStop(convId, peerId);
      sockRef.current?.sendMessageNewAgain(
        {
          chatInfo: { ...data, ...{ convId } } || {},
          additionalInfo: data?.additionalInfo || {},
        }
      );
      // if (!isGroup) {
      //   if (!peerId) return;
      //   return;
      // }

      // if (!groupId) return;
      // if (!groupAllowed) return;
      // sockRef.current?.sendGroupMessage({ convId, groupId, text, files });
    },
    [meId, peerId, convId, isGroup, groupId, chatToken]
  );

  const handleTypingStart = useCallback(() => {
    if (!convId) return;

    // DM
    if (!isGroup) {
      if (!peerId) return;
      sockRef.current?.typingStart(convId, peerId);
      return;
    }

    // GROUP
    if (!groupId || !groupAllowed) return;
    sockRef.current?.groupTypingStart(convId, groupId);
  }, [convId, peerId, isGroup, groupId, groupAllowed]);

  const handleTypingStop = useCallback(() => {
    if (!convId) return;

    // DM
    if (!isGroup) {
      if (!peerId) return;
      sockRef.current?.typingStop(convId, peerId);
      return;
    }

    // GROUP
    if (!groupId || !groupAllowed) return;
    sockRef.current?.groupTypingStop(convId, groupId);
  }, [convId, peerId, isGroup, groupId, groupAllowed]);

  const groupTypingText = useMemo(() => {
    if (!isGroup || !groupAllowed) return "";
    if (!groupTypingIds.length) return "";
    const name = resolveUserName(groupTypingIds[0]);
    return name ? `${name} is typing...` : "";
  }, [isGroup, groupAllowed, groupTypingIds, resolveUserName]);

  const renderTabs = () => (
    <div className="chat-tabs">
      <button className={`chat-tab ${activeTab === "messages" ? "active" : ""}`} onClick={() => setActiveTab("messages")}>
        <FiMessageSquare /> Messages
      </button>

      <button className={`chat-tab ${activeTab === "files" ? "active" : ""}`} onClick={() => setActiveTab("files")}>
        <FiFileText /> Files
      </button>
      {channelName?.category === "project" &&
        <button
          className={`chat-tab ${activeTab === "projectManagement" ? "active" : ""}`}
          onClick={() => setActiveTab("projectManagement")}
        >
          <GoTasklist /> Project Management
        </button>
      }
      <button className={`chat-tab ${activeTab === "canvas" ? "active" : ""}`} onClick={() => setActiveTab("canvas")}>
        <FiPlus /> Add
      </button>
    </div>
  );

  const renderMessagesTab = () => (
    <>
      <ChatMessages
        channelName={channelName}
        onOpenThread={setActiveThread}
        setSelectedProfile={setSelectedProfile}
        messages={messagesState}
        meId={meId}
        meName={meName}
        peerId={peerId}
        peerName={peerName}
        isTyping={!isGroup ? isTyping : false}
        chatToken={chatToken}
        isGroup={isGroup}
        groupAllowed={groupAllowed}
        resolveUserName={resolveUserName}
        groupTypingText={groupTypingText}
      />

      <ChatComposer
        channelName={channelName}
        onSend={handleSendMessage}
        onTypingStart={handleTypingStart}
        onTypingStop={handleTypingStop}
        chatToken={chatToken}
        peerId={peerId}
        convId={convId}
        disabled={isGroup && !groupAllowed}
      />
    </>
  );

  const renderFilesTab = () => (
    <>
      <div className="w-75 m-auto">
        <div className="position-relative my-3">
          <FiSearch className="position-absolute" style={{ top: 10, left: 18 }} />
          <Form.Control
            type="search"
            className="form-control-sm rounded-1 w-100 p-2 ps-5"
            placeholder="Search files ..."
            autoFocus
            style={{ fontSize: 12 }}
          />
        </div>
      </div>

      <div className="chat-messages pb-2 p-0">
        <div className="w-75 m-auto">
          <div className="hstack justify-content-between mb-2">
            <p className="m-0 section-title">Photos and videos</p>

            <p className="m-0 section-title CP" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "View less" : "See all"}
            </p>
          </div>

          <div className={`media-grid mb-3 ${showAll ? "expanded" : "collapsed"}`}>
            <img className="rounded-2" src={channelName?.avatar} alt={channelName?.name} style={{ height: 138 }} />
          </div>

          <p className="section-title fw-bold">Documents</p>

          <ListGroup className="mb-3">
            <ListGroup.Item action style={{ fontSize: 13 }} className="w-100">
              Example file row
            </ListGroup.Item>
          </ListGroup>
        </div>
      </div>
    </>
  );


  // <script
  //   async
  //   src="https://live-chat.galaxbiotech.com/widget.js"
  //   data-page-id="pixa"
  //   // data-bootstrap-href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
  // ></script>



  return (
    <div className="chat-wrapper">
      <div className="chat-main">
        <ChatHeader
          activeChannel={channelName}
          channelName={channelName}
          memberList={members}
          currentUserData={currentUserData}
          getBookmarkTeamProjectList={getBookmarkTeamProjectList}
        />

        {renderTabs()}

        {activeTab === "messages" && renderMessagesTab()}
        {activeTab === "files" && renderFilesTab()}
        {activeTab === "projectManagement" && <ProjectManagement gpId={channelName?.gpId} />}
        {activeTab === "canvas" && <ThreadPanel activeThread={activeThread} />}
      </div>
    </div>
  );
};

export default ChatLayout;
